import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../config/supabase';
import { useAuthStore } from '../store/authStore';
import { useAdminScope } from '../hooks/useAdminScope';
import { Plus, Edit2, Trash2, Send, Eye, Calendar, Users, X, Save, AlertCircle, GripVertical, Search, XCircle, Copy, RotateCcw, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSurveyProtection } from '../hooks/useSurveyProtection';
import { SurveyHealthBadge } from '../components/SurveyHealthBadge';
import { DeploySurveyDialog } from '../components/DeploySurveyDialog';
import {
  EMPLOYMENT_STATUS_OPTIONS, EMPLOYER_TYPE_OPTIONS, JOB_RELEVANCE_OPTIONS,
  SALARY_RANGE_OPTIONS, EMPLOYMENT_LENGTH_OPTIONS, FURTHER_STUDIES_OPTIONS,
} from '../constants/profileOptions';

// Profile field mapping for survey integration
const PROFILE_FIELD_OPTIONS = [
  { value: '', label: 'None (Survey data only)', compatibleWith: ['rating', 'multiple_choice', 'text', 'yes_no'] },
  // Text fields (free-form input)
  { value: 'job_position', label: 'Job Position/Title', compatibleWith: ['text'] },
  { value: 'field_of_work', label: 'Field of Work/Industry', compatibleWith: ['text'] },
  { value: 'unemployment_reasons', label: 'Unemployment Reasons', compatibleWith: ['text'] },
  // Choice fields (enums/specific values)
  { value: 'current_employment_status', label: 'Employment Status', compatibleWith: ['multiple_choice', 'yes_no'] },
  { value: 'employer_type', label: 'Employer Type', compatibleWith: ['multiple_choice'] },
  { value: 'job_relevance', label: 'Job Relevance to Degree', compatibleWith: ['multiple_choice'] },
  { value: 'salary_range', label: 'Salary Range', compatibleWith: ['multiple_choice'] },
  // Choice fields continued
  { value: 'employment_length', label: 'Employment Length', compatibleWith: ['multiple_choice'] },
  // Array fields (for repeated answers - text only as they accept free-form comma-separated values)
  { value: 'further_studies', label: 'Further Studies', compatibleWith: ['multiple_choice'] },
  { value: 'skills_acquired', label: 'Skills Acquired', compatibleWith: ['text'] },
  { value: 'career_progression', label: 'Career Progression', compatibleWith: ['text'] },
  { value: 'job_search_methods', label: 'Job Search Methods', compatibleWith: ['text'] },
];

// Pre-configured question templates for profile fields
const PROFILE_FIELD_TEMPLATES: Record<string, any> = {
  current_employment_status: {
    multiple_choice: {
      text: 'What is your current employment status?',
      options: [...EMPLOYMENT_STATUS_OPTIONS],
      required: true
    },
    yes_no: {
      text: 'Are you currently employed?',
      required: true
    }
  },
  employer_type: {
    multiple_choice: {
      text: 'What type of organization do you work for?',
      options: [...EMPLOYER_TYPE_OPTIONS],
      required: true
    }
  },
  job_relevance: {
    multiple_choice: {
      text: 'How relevant is your current job to your degree?',
      options: [...JOB_RELEVANCE_OPTIONS],
      required: true
    }
  },
  salary_range: {
    multiple_choice: {
      text: 'What is your current monthly salary range?',
      options: [...SALARY_RANGE_OPTIONS],
      required: false
    }
  },
  employment_length: {
    multiple_choice: {
      text: 'How long have you been employed in your current position?',
      options: [...EMPLOYMENT_LENGTH_OPTIONS],
      required: true
    }
  },
  job_position: {
    text: {
      text: 'What is your current job position/title?',
      required: true
    }
  },
  field_of_work: {
    text: {
      text: 'What is your current field of work or industry?',
      required: true
    }
  },
  unemployment_reasons: {
    text: {
      text: 'If unemployed, please share your reasons or current situation',
      required: false
    }
  },
  further_studies: {
    multiple_choice: {
      text: 'Are you pursuing further studies?',
      options: [...FURTHER_STUDIES_OPTIONS],
      required: false
    }
  },
  skills_acquired: {
    text: {
      text: 'What new skills have you acquired since graduation?',
      required: false
    }
  },
  career_progression: {
    text: {
      text: 'Describe your career progression since graduation',
      required: false
    }
  },
  job_search_methods: {
    text: {
      text: 'What methods did you use to find your current job?',
      required: false
    }
  }
};

// Helper function to apply template to question
const applyProfileFieldTemplate = (question: any, profileField: string): any => {
  if (!profileField || !PROFILE_FIELD_TEMPLATES[profileField]) {
    return question;
  }

  const templates = PROFILE_FIELD_TEMPLATES[profileField];
  const template = templates[question.type];

  if (!template) {
    // No template for this type, return as is
    return question;
  }

  // Merge template with existing question, preserving question id
  return {
    ...question,
    ...template,
    id: question.id, // Preserve the question ID
    profile_field: profileField // Ensure profile_field is set
  };
};

// Helper function to get compatible profile fields for a question type
const getCompatibleProfileFields = (questionType: string) => {
  return PROFILE_FIELD_OPTIONS.filter(option => 
    option.compatibleWith.includes(questionType)
  );
};

// Helper function to provide guidance on field mapping


// Interfaces
interface Question {
  id: string;
  type: 'rating' | 'multiple_choice' | 'text' | 'yes_no';
  text: string;
  options?: string[]; // For multiple choice
  required: boolean;
  peo_ids?: string[]; // Link to PEOs/POs for scoring (can link to multiple)
  rating_scale?: number; // For rating questions (default 10)
  profile_field?: string; // Link to alumni_profiles field for auto-update
}

interface Survey {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  target_batch?: number[];
  target_programs?: string[];
  target_departments?: string[];
  status: 'draft' | 'active' | 'closed' | 'archived';
  created_by: string;
  created_at: string;
  updated_at: string;
  deadline?: string;
  response_count?: number;
}

// Helper function to check if survey is expired
const isSurveyExpired = (survey: Survey): boolean => {
  if (!survey.deadline) return false;
  return new Date(survey.deadline) < new Date();
};

interface SurveyResponse {
  id: string;
  survey_id: string;
  user_id: string;
  answers: Record<string, any>;
  submitted_at: string;
  alumni_profiles?: {
    first_name: string;
    last_name: string;
    program?: string;
    year_graduated?: number;
  };
}

interface PEO {
  id: string;
  program_id: string;
  peo_number: number;
  title: string;
  description: string;
  outcome_type: 'PEO' | 'PO';
  programs?: {
    code: string;
    name: string;
  };
}

interface Program {
  id: string;
  name: string;
  code: string;
  department_id: string;
}

interface Department {
  id: string;
  name: string;
}

const ManageSurveys: React.FC = () => {
  const { user } = useAuthStore();
  const { isSuperAdmin, allowedDepartments, loading: scopeLoading } = useAdminScope();
  const { getSurveysHealth } = useSurveyProtection();
  
  // State
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPendingResponses, setTotalPendingResponses] = useState<number>(0);
  const [surveyHealth, setSurveyHealth] = useState<Record<string, any>>({});
  
  // Search and Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Survey['status']>('active');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [showGuide, setShowGuide] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isRedeployMode, setIsRedeployMode] = useState(false);
  
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [surveyResponses, setSurveyResponses] = useState<SurveyResponse[]>([]);
  const [responseSearch, setResponseSearch] = useState('');
  const [viewSurveyStats, setViewSurveyStats] = useState<{
    totalResponses: number;
    completionRate: number;
    pendingResponses: number;
  } | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<Partial<Survey>>({
    title: '',
    description: '',
    questions: [],
    status: 'draft',
    target_batch: [],
    target_programs: []
  });
  
  // Deploy form
  const [deployTarget, setDeployTarget] = useState<'all' | 'specific'>('all');
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [deployDeadline, setDeployDeadline] = useState<string>('');
  
  // Reference data
  const [peos, setPeos] = useState<PEO[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Fetch surveys
  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch surveys with response counts
      const { data: surveysData, error: surveysError } = await supabaseClient
        .from('surveys')
        .select(`
          *,
          survey_responses(count)
        `)
        .order('created_at', { ascending: false });

      if (surveysError) throw surveysError;

      // Transform data to include response count
      const transformedSurveys = surveysData?.map(survey => ({
        ...survey,
        response_count: survey.survey_responses?.[0]?.count || 0,
        survey_responses: undefined // Remove from object
      })) || [];

      // Auto-close expired active surveys
      const expiredSurveys = transformedSurveys.filter(s => s.status === 'active' && isSurveyExpired(s));
      
      let finalSurveys = transformedSurveys;
      
      if (expiredSurveys.length > 0) {
        console.log(`Auto-closing ${expiredSurveys.length} expired surveys:`, expiredSurveys.map(s => s.title));
        
        const { error: updateError } = await supabaseClient
          .from('surveys')
          .update({ status: 'closed' })
          .in('id', expiredSurveys.map(s => s.id));
        
        if (updateError) {
          console.error('Error auto-closing expired surveys:', updateError);
        } else {
          // Update local state to reflect closed status
          finalSurveys = transformedSurveys.map(s => 
            expiredSurveys.some(exp => exp.id === s.id) 
              ? { ...s, status: 'closed' as const } 
              : s
          );
        }
      }
      
      setSurveys(finalSurveys);

      // For dept admins, overwrite response_count with only their dept's responses
      if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
        const surveyIds = finalSurveys.map(s => s.id);
        if (surveyIds.length > 0) {
          // Get user IDs of dept alumni
          const { data: deptAlumni } = await supabaseClient
            .from('alumni_profiles')
            .select('user_id')
            .in('department', allowedDepartments)
            .eq('approval_status', 'approved');
          const deptUserIds = (deptAlumni || []).map((a: any) => a.user_id);
          if (deptUserIds.length > 0) {
            const { data: deptResponses } = await supabaseClient
              .from('survey_responses')
              .select('survey_id')
              .in('survey_id', surveyIds)
              .in('user_id', deptUserIds);
            const countMap: Record<string, number> = {};
            (deptResponses || []).forEach((r: any) => {
              countMap[r.survey_id] = (countMap[r.survey_id] || 0) + 1;
            });
            finalSurveys = finalSurveys.map(s => ({
              ...s,
              response_count: countMap[s.id] ?? 0
            }));
            setSurveys(finalSurveys);
          } else {
            setSurveys(finalSurveys.map(s => ({ ...s, response_count: 0 })));
          }
        }
      }

      // Calculate total pending responses for active surveys only
      calculateTotalPendingResponses(finalSurveys);
    } catch (err: any) {
      console.error('Error fetching surveys:', err);
      setError(err.message);
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total pending responses across all active surveys
  const calculateTotalPendingResponses = async (surveysList: Survey[]) => {
    try {
      const activeSurveys = surveysList.filter(s => s.status === 'active');
      
      if (activeSurveys.length === 0) {
        setTotalPendingResponses(0);
        return;
      }

      // Fetch all responses for active surveys
      const { data: allResponses, error: responsesError } = await supabaseClient
        .from('survey_responses')
        .select('survey_id, user_id')
        .in('survey_id', activeSurveys.map(s => s.id));

      if (responsesError) throw responsesError;

      // Create a map of user_id -> Set<survey_id> for surveys they've responded to
      const respondedMap = new Map<string, Set<string>>();
      (allResponses || []).forEach(response => {
        if (!respondedMap.has(response.user_id)) {
          respondedMap.set(response.user_id, new Set());
        }
        respondedMap.get(response.user_id)!.add(response.survey_id);
      });

      // Track unique alumni who have at least one pending survey
      const alumniWithPending = new Set<string>();

      for (const survey of activeSurveys) {
        // Get eligible alumni for this survey
        let alumniQuery = supabaseClient
          .from('alumni_profiles')
          .select('user_id')
          .eq('approval_status', 'approved');

        // For dept admins, scope to their department's alumni
        if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
          alumniQuery = alumniQuery.in('department', allowedDepartments);
        }

        // Apply target_departments filter if set (intersection with survey targeting)
        if (survey.target_departments && survey.target_departments.length > 0) {
          alumniQuery = alumniQuery.in('department', survey.target_departments);
        }

        // Apply program filter if set
        if (survey.target_programs && survey.target_programs.length > 0) {
          alumniQuery = alumniQuery.in('program', survey.target_programs);
        }

        // Apply year filter if set
        if (survey.target_batch && survey.target_batch.length > 0) {
          alumniQuery = alumniQuery.in('year_graduated', survey.target_batch);
        }

        const { data: eligibleAlumni, error: alumniError } = await alumniQuery;
        
        if (alumniError) throw alumniError;

        // For each eligible alumni, check if they've responded to this survey
        (eligibleAlumni || []).forEach(alumni => {
          const hasResponded = respondedMap.get(alumni.user_id)?.has(survey.id);
          if (!hasResponded) {
            // This alumni hasn't responded to this survey, so they have at least one pending
            alumniWithPending.add(alumni.user_id);
          }
        });
      }

      // Count unique alumni with at least one pending survey
      setTotalPendingResponses(alumniWithPending.size);
    } catch (err) {
      console.error('Error calculating pending responses:', err);
      setTotalPendingResponses(0);
    }
  };

  // Fetch reference data
  const fetchReferenceData = async () => {
    try {
      // Fetch PEOs and POs with program information
      const { data: peosData } = await supabaseClient
        .from('program_educational_outcomes')
        .select(`
          *,
          programs(
            code,
            name
          )
        `)
        .eq('is_active', true)
        .order('outcome_type', { ascending: false })
        .order('peo_number');
      
      setPeos(peosData || []);

      // Fetch programs
      const { data: programsData } = await supabaseClient
        .from('programs')
        .select('id, name, code, department_id')
        .order('name');
      
      // Fetch departments
      const { data: depsData } = await supabaseClient
        .from('departments')
        .select('id, name')
        .order('name');

      const deptFilter = !isSuperAdmin && allowedDepartments && allowedDepartments.length > 0;

      // For dept admins, scope departments and programs to their allowed scope
      const filteredDepts = deptFilter
        ? (depsData?.filter((d: any) => allowedDepartments!.includes(d.name)) ?? [])
        : (depsData ?? []);
      setDepartments(filteredDepts);

      const allowedDeptIds = filteredDepts.map((d: any) => d.id);
      const filteredPrograms = deptFilter
        ? (programsData?.filter((p: any) => allowedDeptIds.includes(p.department_id)) ?? [])
        : (programsData ?? []);
      setPrograms(filteredPrograms);

      // Fetch available graduation years (scoped to dept admin's approved alumni)
      let yearsQuery = supabaseClient
        .from('alumni_profiles')
        .select('year_graduated')
        .eq('approval_status', 'approved')
        .order('year_graduated', { ascending: false });
      if (deptFilter) yearsQuery = yearsQuery.in('department', allowedDepartments!);
      const { data: yearsData } = await yearsQuery;
      
      const uniqueYears = Array.from(new Set(yearsData?.map((y: any) => y.year_graduated).filter(Boolean))) as number[];
      setAvailableYears(uniqueYears);

    } catch (err: any) {
      console.error('Error fetching reference data:', err);
    }
  };

  const loadSurveyHealth = async () => {
    try {
      const healthData = await getSurveysHealth();
      const healthMap: Record<string, any> = {};
      healthData.forEach((h: any) => {
        healthMap[h.id] = h;
      });
      setSurveyHealth(healthMap);
    } catch (err) {
      console.error('Failed to load survey health:', err);
    }
  };

  useEffect(() => {
    if (scopeLoading) return;
    fetchSurveys();
    fetchReferenceData();
    loadSurveyHealth();

    // Set up realtime subscription for response updates
    const subscription = supabaseClient
      .channel('survey_responses_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'survey_responses' },
        () => {
          fetchSurveys(); // Refresh on any response change
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [isSuperAdmin, allowedDepartments, scopeLoading]);

  // Compute which surveys are visible to this admin (dept scoping + cross-dept targeting)
  const visibleSurveys = React.useMemo(() => {
    if (isSuperAdmin || !allowedDepartments || allowedDepartments.length === 0) return surveys;
    const allowedDeptIds = departments
      .filter(d => allowedDepartments!.includes(d.name))
      .map(d => d.id);
    const deptProgramCodes = new Set(
      programs
        .filter((p: any) => allowedDeptIds.includes(p.department_id))
        .map(p => p.code)
    );
    return surveys.filter(survey => {
      if (survey.created_by === user?.id) return true;
      // No targeting = all-alumni survey = visible to all
      const noTargeting =
        (!survey.target_departments || survey.target_departments.length === 0) &&
        (!survey.target_programs || survey.target_programs.length === 0);
      if (noTargeting) return true;
      // Targeted to this admin's department(s)
      if (survey.target_departments?.some(d => allowedDepartments!.includes(d))) return true;
      // Targeted to programs that belong to this admin's department(s)
      if (survey.target_programs?.some(p => deptProgramCodes.has(p))) return true;
      return false;
    });
  }, [surveys, isSuperAdmin, allowedDepartments, programs, departments, user?.id]);

  // Helper: can this admin modify (edit/delete/deploy/close) a given survey?
  const canModify = (survey: Survey) => {
    if (isSuperAdmin) return true;
    if (survey.created_by === user?.id) return true;
    // Department admins can deploy/manage surveys targeting their programs
    if (allowedDepartments && allowedDepartments.length > 0) {
      const allowedDeptIds = departments
        .filter(d => allowedDepartments.includes(d.name))
        .map(d => d.id);
      const deptProgramCodes = new Set(
        programs
          .filter((p: any) => allowedDeptIds.includes(p.department_id))
          .map(p => p.code)
      );
      if (survey.target_programs?.some(p => deptProgramCodes.has(p))) return true;
      if (survey.target_departments?.some(d => allowedDepartments.includes(d))) return true;
    }
    return false;
  };

  // Calculate stats
  const stats = {
    total: visibleSurveys.length,
    active: visibleSurveys.filter(s => s.status === 'active').length,
    totalResponses: visibleSurveys.filter(s => s.status === 'active').reduce((sum, s) => sum + (s.response_count || 0), 0),
    pendingResponses: totalPendingResponses
  };

  // Open create modal
  const openCreateModal = () => {
    setFormData({
      title: '',
      description: '',
      questions: [],
      status: 'draft',
      target_batch: [],
      target_programs: []
    });
    setShowCreateModal(true);
  };

  // Open edit modal
  const openEditModal = (survey: Survey) => {
    // Prevent editing closed or archived surveys
    if (survey.status === 'closed' || survey.status === 'archived') {
      toast.error(`Cannot edit ${survey.status} surveys. ${survey.status === 'closed' ? 'Reopen to draft first.' : 'Duplicate the survey to create a new version.'}`);
      return;
    }

    setSelectedSurvey(survey);
    setFormData({
      title: survey.title,
      description: survey.description,
      questions: survey.questions || [],
      status: survey.status,
      target_batch: survey.target_batch || [],
      target_programs: survey.target_programs || [],
      deadline: survey.deadline
    });
    setShowEditModal(true);
  };

  // Save survey (create or update)
  const handleSaveSurvey = async (saveAndRedeploy: boolean = false) => {
    try {
      // Validation
      if (!formData.title?.trim()) {
        toast.error('Survey title is required');
        return;
      }

      if (!formData.questions || formData.questions.length === 0) {
        toast.error('Add at least one question');
        return;
      }

      // Validate questions
      for (const q of formData.questions) {
        if (!q.text?.trim()) {
          toast.error('All questions must have text');
          return;
        }
        if (q.type === 'multiple_choice' && (!q.options || q.options.length < 2)) {
          toast.error('Multiple choice questions need at least 2 options');
          return;
        }
      }

      // For editing active surveys, determine the final status
      let finalStatus = formData.status || 'draft';
      if (showEditModal && selectedSurvey?.status === 'active' && saveAndRedeploy) {
        finalStatus = 'active'; // Keep it active for redeploy
      } else if (showEditModal && selectedSurvey?.status === 'active' && !saveAndRedeploy) {
        finalStatus = 'draft'; // Save as draft when explicitly chosen
      }

      const surveyData: any = {
        title: formData.title,
        description: formData.description || '',
        questions: formData.questions,
        status: finalStatus,
        target_batch: saveAndRedeploy && selectedSurvey ? selectedSurvey.target_batch : (formData.target_batch || []),
        target_programs: saveAndRedeploy && selectedSurvey ? selectedSurvey.target_programs : (formData.target_programs || []),
        deadline: formData.deadline || null, // Preserve the actual deadline
        created_by: user?.id
      };

      if (showEditModal && selectedSurvey) {
        // Update existing survey
        console.log('=== SURVEY UPDATE DEBUG ===');
        console.log('Survey ID being updated:', selectedSurvey.id);
        console.log('Survey Title:', selectedSurvey.title);
        console.log('Save and Redeploy:', saveAndRedeploy);
        console.log('Survey data being sent:', surveyData);
        
        const { data: updatedData, error } = await supabaseClient
          .from('surveys')
          .update(surveyData)
          .eq('id', selectedSurvey.id)
          .select();

        if (error) throw error;
        
        console.log('Updated survey result:', updatedData);
        console.log('Number of surveys updated:', updatedData?.length || 0);

        // Invalidate existing responses using timestamp approach (no deletion needed)
        if (saveAndRedeploy) {
          console.log('Survey redeployed - existing responses will be reset');
          
          // Check how many responses will be affected (for reporting)
          const { count } = await supabaseClient
            .from('survey_responses')
            .select('*', { count: 'exact', head: true })
            .eq('survey_id', selectedSurvey.id);
          
          if (count && count > 0) {
            console.log(`${count} existing responses will be reset by the survey update`);
            toast.success(
              `Survey redeployed! ${count} response${count > 1 ? 's' : ''} reset.`, 
              { duration: 4000 }
            );
          } else {
            toast.success('Survey redeployed successfully!');
          }
        } else if (selectedSurvey.status === 'active') {
          toast.success('Survey saved as draft.');
        } else {
          toast.success('Survey updated successfully.');
        }
      } else {
        // Create new survey
        const { error } = await supabaseClient
          .from('surveys')
          .insert(surveyData);

        if (error) throw error;
        toast.success('Survey created successfully');
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      fetchSurveys();
    } catch (err: any) {
      console.error('Error saving survey:', err);
      toast.error(err.message || 'Failed to save survey');
    }
  };

  // Add question
  const addQuestion = (type: Question['type']) => {
    const newQuestion: Question = {
      id: `q_${Date.now()}`,
      type,
      text: '',
      required: true,
      rating_scale: type === 'rating' ? 10 : undefined,
      options: type === 'multiple_choice' ? ['', ''] : undefined,
      profile_field: '' // Default to no profile field sync
    };

    setFormData({
      ...formData,
      questions: [...(formData.questions || []), newQuestion]
    });
  };

  // Update question
  const updateQuestion = (index: number, updates: Partial<Question>) => {
    const newQuestions = [...(formData.questions || [])];
    const currentQuestion = newQuestions[index];
    const updatedQuestion = { ...currentQuestion, ...updates };
    
    // If question type is changing, validate profile_field compatibility
    if (updates.type && updatedQuestion.profile_field) {
      const compatibleFields = getCompatibleProfileFields(updates.type);
      const isCompatible = compatibleFields.some(field => field.value === updatedQuestion.profile_field);
      
      if (!isCompatible) {
        // Clear incompatible profile_field
        updatedQuestion.profile_field = '';
        toast.error(`Profile field cleared - not compatible with ${updates.type} questions`);
      }
    }
    
    newQuestions[index] = updatedQuestion;
    setFormData({ ...formData, questions: newQuestions });
  };

  // Delete question
  const deleteQuestion = (index: number) => {
    const newQuestions = formData.questions?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, questions: newQuestions });
  };

  // Move question
  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const newQuestions = [...(formData.questions || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newQuestions.length) return;
    
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    setFormData({ ...formData, questions: newQuestions });
  };

  // Open deploy dialog
  const openDeployModal = (survey: Survey) => {
    setSelectedSurvey(survey);
    setShowDeployDialog(true);
  };

  // Preview alumni count before deployment (DEPRECATED - no longer used with new DeploySurveyDialog)
  // const fetchAlumniCountPreview = async () => {
  //   try {
  //     const { count, error } = await supabaseClient
  //       .from('alumni_profiles')
  //       .select('id', { count: 'exact', head: true });

  //     if (error) {
  //       console.error('Error fetching alumni count preview:', error);
  //       return;
  //     }

  //     console.log('Total alumni in database:', count);
  //   } catch (err) {
  //     console.error('Preview count error:', err);
  //   }
  // };

  // Handle deployment
  const handleDeploy = async () => {
    try {
      if (!selectedSurvey) return;

      // If in redeploy mode, save the survey changes first
      if (isRedeployMode) {
        const surveyData: any = {
          title: formData.title,
          description: formData.description,
          questions: formData.questions,
          status: 'active',
          target_batch: deployTarget === 'specific' ? selectedYears : [],
          target_programs: deployTarget === 'specific' ? selectedPrograms : [],
          deadline: deployDeadline ? new Date(deployDeadline).toISOString() : null,
          updated_at: new Date().toISOString(),
          redeployed_at: new Date().toISOString(),
        };

        const { error: updateError } = await supabaseClient
          .from('surveys')
          .update(surveyData)
          .eq('id', selectedSurvey.id);

        if (updateError) throw updateError;

        // Delete all previous responses for this survey when redeploying
        console.log('[REDEPLOY] Attempting to delete responses for survey:', selectedSurvey.id);
        
        // First, check what responses exist
        const { data: existingResponses, error: checkError } = await supabaseClient
          .from('survey_responses')
          .select('*')
          .eq('survey_id', selectedSurvey.id);
        
        console.log('[REDEPLOY] Existing responses before delete:', existingResponses, checkError);
        
        // Now delete them
        const { count, error: deleteError } = await supabaseClient
          .from('survey_responses')
          .delete({ count: 'exact' })
          .eq('survey_id', selectedSurvey.id);

        console.log('[REDEPLOY] Delete result:', { count, deleteError });

        if (deleteError) {
          console.error('[REDEPLOY] Error deleting responses:', deleteError);
          toast.error('Survey updated but failed to reset responses');
        } else if (count && count > 0) {
          toast.success(
            `Survey redeployed! ${count} response${count > 1 ? 's' : ''} deleted.`,
            { duration: 4000 }
          );
        } else {
          toast.success('Survey redeployed successfully! No old responses found.');
        }

        setIsRedeployMode(false);
        setShowDeployDialog(false);
        fetchSurveys();
        return;
      }

      // Normal deploy mode (for draft surveys)
      const { error: updateError } = await supabaseClient
        .from('surveys')
        .update({
          status: 'active',
          target_batch: deployTarget === 'specific' ? selectedYears : [],
          target_programs: deployTarget === 'specific' ? selectedPrograms : [],
          deadline: deployDeadline ? new Date(deployDeadline).toISOString() : null
        })
        .eq('id', selectedSurvey.id);

      if (updateError) throw updateError;

      // Count target alumni for notification purposes
      let countQuery = supabaseClient
        .from('alumni_profiles')
        .select('id', { count: 'exact', head: true });

      if (deployTarget === 'specific') {
        if (selectedYears.length > 0) {
          countQuery = countQuery.in('year_graduated', selectedYears);
        }
        if (selectedPrograms.length > 0) {
          // Match against both program (string) and program_id if it exists
          // Use program codes from target_programs array
          countQuery = countQuery.in('program', selectedPrograms);
        }
        if (selectedDepartments.length > 0) {
          countQuery = countQuery.in('department', selectedDepartments);
        }
      }

      const { count, error: countError } = await countQuery;

      if (countError) {
        console.error('Error counting alumni:', countError);
      }

      const alumniCount = count || 0;

      // Log email targets (placeholder for actual email service)
      console.log('📧 Survey deployed successfully');
      console.log('Target alumni count:', alumniCount);
      console.log('Target:', deployTarget === 'all' ? 'All alumni' : {
        years: selectedYears,
        programs: selectedPrograms,
        departments: selectedDepartments
      });

      // Show success message with count if available
      if (alumniCount > 0) {
        toast.success(`Survey deployed successfully to ${alumniCount} alumni!`);
      } else {
        toast.success('Survey deployed successfully! Alumni will see it when they log in.');
      }
      
      setShowDeployDialog(false);
      fetchSurveys();
    } catch (err: any) {
      console.error('Error deploying survey:', err);
      toast.error(err.message || 'Failed to deploy survey');
    }
  };

  // Open close confirmation
  const openCloseConfirm = (survey: Survey) => {
    setSelectedSurvey(survey);
    setShowCloseConfirm(true);
  };

  // Handle close survey
  const handleCloseSurvey = async () => {
    try {
      if (!selectedSurvey) return;

      const { error } = await supabaseClient
        .from('surveys')
        .update({ status: 'closed' })
        .eq('id', selectedSurvey.id);

      if (error) throw error;
      toast.success('Survey closed successfully');
      setShowCloseConfirm(false);
      fetchSurveys();
    } catch (err: any) {
      console.error('Error closing survey:', err);
      toast.error(err.message || 'Failed to close survey');
    }
  };

  // Reopen closed survey to draft
  const handleReopenToDraft = async (survey: Survey) => {
    try {
      const { error } = await supabaseClient
        .from('surveys')
        .update({ status: 'draft' })
        .eq('id', survey.id);

      if (error) throw error;
      toast.success('Survey reopened as draft');
      setShowViewModal(false);
      fetchSurveys();
    } catch (err: any) {
      console.error('Error reopening survey:', err);
      toast.error(err.message || 'Failed to reopen survey');
    }
  };

  // Duplicate survey
  const handleDuplicateSurvey = async (survey: Survey) => {
    try {
      const { error } = await supabaseClient
        .from('surveys')
        .insert({
          title: `${survey.title} (Copy)`,
          description: survey.description,
          questions: survey.questions,
          status: 'draft',
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      toast.success('Survey duplicated successfully');
      setShowViewModal(false);
      fetchSurveys();
    } catch (err: any) {
      console.error('Error duplicating survey:', err);
      toast.error(err.message || 'Failed to duplicate survey');
    }
  };

  // Open delete confirmation
  const openDeleteConfirm = (survey: Survey) => {
    setSelectedSurvey(survey);
    setShowDeleteConfirm(true);
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      if (!selectedSurvey) return;

      // If survey is archived, allow permanent deletion
      if (selectedSurvey.status === 'archived') {
        // Delete all responses first
        const { error: responseError } = await supabaseClient
          .from('survey_responses')
          .delete()
          .eq('survey_id', selectedSurvey.id);

        if (responseError) throw responseError;

        // Then delete the survey
        const { error } = await supabaseClient
          .from('surveys')
          .delete()
          .eq('id', selectedSurvey.id);

        if (error) throw error;
        toast.success('Survey and all responses permanently deleted');
      } else if (
        selectedSurvey.status === 'active' ||
        selectedSurvey.status === 'closed' ||
        (selectedSurvey.response_count && selectedSurvey.response_count > 0)
      ) {
        // Archive active/closed surveys or surveys with responses
        const { error } = await supabaseClient
          .from('surveys')
          .update({ status: 'archived' })
          .eq('id', selectedSurvey.id);

        if (error) throw error;
        toast.success('Survey archived');
      } else {
        // Hard delete for draft surveys without responses
        const { error } = await supabaseClient
          .from('surveys')
          .delete()
          .eq('id', selectedSurvey.id);

        if (error) throw error;
        toast.success('Survey deleted successfully');
      }

      setShowDeleteConfirm(false);
      fetchSurveys();
    } catch (err: any) {
      console.error('Error deleting survey:', err);
      toast.error(err.message || 'Failed to delete survey');
    }
  };

  // View responses
  const viewResponses = async (survey: Survey) => {
    try {
      // Check if survey is expired and auto-close if needed
      if (survey.status === 'active' && isSurveyExpired(survey)) {
        console.log(`Survey "${survey.title}" is expired, auto-closing...`);
        
        const { error: updateError } = await supabaseClient
          .from('surveys')
          .update({ status: 'closed' })
          .eq('id', survey.id);
        
        if (updateError) {
          console.error('Error auto-closing expired survey:', updateError);
        } else {
          // Update local state
          setSurveys(prev => prev.map(s => 
            s.id === survey.id ? { ...s, status: 'closed' as const } : s
          ));
          survey.status = 'closed';
          toast.success('Survey has been automatically closed due to expired deadline');
        }
      }
      
      setSelectedSurvey(survey);
      setLoading(true);

      // First, fetch the survey responses (scoped to dept admin's alumni)
      let responsesQuery = supabaseClient
        .from('survey_responses')
        .select('*')
        .eq('survey_id', survey.id)
        .order('submitted_at', { ascending: false });

      // For dept admins, filter to only their department's alumni
      if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
        const { data: deptAlumni } = await supabaseClient
          .from('alumni_profiles')
          .select('user_id')
          .in('department', allowedDepartments)
          .eq('approval_status', 'approved');
        const deptUserIds = (deptAlumni || []).map((a: any) => a.user_id);
        if (deptUserIds.length > 0) {
          responsesQuery = responsesQuery.in('user_id', deptUserIds);
        } else {
          // dept admin has no approved alumni — return empty
          setSurveyResponses([]);
          setViewSurveyStats({ totalResponses: 0, completionRate: 0, pendingResponses: 0 });
          setSelectedSurvey(survey);
          setShowViewModal(true);
          setLoading(false);
          return;
        }
      }

      const { data: responsesData, error: responsesError } = await responsesQuery;

      if (responsesError) throw responsesError;

      // If there are responses, fetch the alumni profiles for those users
      let enrichedResponses: SurveyResponse[] = [];
      
      if (responsesData && responsesData.length > 0) {
        const userIds = responsesData.map(r => r.user_id);
        
        const { data: profilesData, error: profilesError } = await supabaseClient
          .from('alumni_profiles')
          .select('user_id, first_name, last_name, program, year_graduated')
          .in('user_id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        }

        // Create a map of user_id to profile
        const profileMap = new Map(
          (profilesData || []).map(p => [p.user_id, p])
        );

        // Enrich responses with profile data
        enrichedResponses = responsesData.map(response => ({
          ...response,
          alumni_profiles: profileMap.get(response.user_id) || undefined
        }));
      }

      setSurveyResponses(enrichedResponses);

      // Calculate stats - count eligible alumni based on survey targeting
      let eligibleCount = 0;
      
      // Build query based on targeting options
      let countQuery = supabaseClient
        .from('alumni_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('approval_status', 'approved');
      
      // For dept admins, always scope eligible count to their department
      if (!isSuperAdmin && allowedDepartments && allowedDepartments.length > 0) {
        countQuery = countQuery.in('department', allowedDepartments);
      }

      // Also apply survey's target_departments if set
      if (survey.target_departments && survey.target_departments.length > 0) {
        countQuery = countQuery.in('department', survey.target_departments);
      }
      
      // Apply program filter if specified
      if (survey.target_programs && survey.target_programs.length > 0) {
        countQuery = countQuery.in('program', survey.target_programs);
      }
      
      // Apply year filter if specified
      if (survey.target_batch && survey.target_batch.length > 0) {
        countQuery = countQuery.in('year_graduated', survey.target_batch);
      }
      
      const { count } = await countQuery;
      eligibleCount = count || 0;

      if (enrichedResponses.length > 0) {
        setViewSurveyStats({
          totalResponses: enrichedResponses.length,
          completionRate: eligibleCount > 0 ? Math.round((enrichedResponses.length / eligibleCount) * 100) : 0,
          pendingResponses: eligibleCount > 0 ? eligibleCount - enrichedResponses.length : 0
        });
      } else {
        setViewSurveyStats({
          totalResponses: 0,
          completionRate: 0,
          pendingResponses: eligibleCount || 0
        });
      }

      setShowViewModal(true);
    } catch (err: any) {
      console.error('Error fetching responses:', err);
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  // Status badge component
  const StatusBadge: React.FC<{ status: Survey['status'] }> = ({ status }) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-gray-100 text-gray-800',
      archived: 'bg-gray-100 text-gray-600'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Loading state
  if (loading && surveys.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading surveys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto py-8">
      {/* Header */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden mb-6">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Manage Surveys</h1>
              <p className="mt-1 text-sm text-blue-200">Create and deploy surveys to alumni</p>
            </div>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-300 transition flex items-center gap-2 font-semibold shrink-0"
          >
            <Plus className="w-5 h-5" />
            Create Survey
          </button>
        </div>
      </div>

      {/* Survey Lifecycle Guide - Collapsible */}
      <div className="mb-6">
        <button
          onClick={() => setShowGuide(!showGuide)}
          className="w-full flex items-center justify-between px-4 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition text-sm font-medium text-blue-900"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>Survey Lifecycle & Health Guide</span>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${showGuide ? 'rotate-180' : ''}`} />
        </button>
        
        {showGuide && (
          <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-blue-800 mb-3">
              <div className="flex items-start gap-2">
                <span className="font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Draft</span>
                <span>Create and edit freely. Deploy when ready.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold bg-green-100 text-green-800 px-2 py-0.5 rounded">Active</span>
                <span>Alumni can respond. Edit with "Save & Redeploy" (resets responses).</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold bg-gray-100 text-gray-800 px-2 py-0.5 rounded">Closed</span>
                <span>Read-only. Responses preserved. Can reopen to draft or duplicate.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Archived</span>
                <span>Historical data. Read-only. Duplicate to reuse. Can permanently delete.</span>
              </div>
            </div>
            <div className="pt-3 border-t border-blue-200">
              <p className="text-xs font-semibold text-blue-900 mb-2">Survey Health Indicators:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-blue-800">
                <div className="flex items-start gap-2">
                  <span className="font-semibold bg-green-100 text-green-800 px-2 py-0.5 rounded">Healthy</span>
                  <span>All PEO/PO references valid. Ready to deploy.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Warning</span>
                  <span>Draft survey has broken PEO/PO references. Fix before deploying.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold bg-red-100 text-red-800 px-2 py-0.5 rounded">Critical</span>
                  <span>Active survey with broken references. Data integrity compromised.</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search surveys by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Department and Program Filters */}
      <div className="mb-6 flex flex-wrap gap-3">
        {/* Department Filter (Super Admin only) */}
        {isSuperAdmin && departments.length > 0 && (
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Filter by Department
            </label>
            <select
              value={departmentFilter}
              onChange={(e) => {
                setDepartmentFilter(e.target.value);
                setProgramFilter('all'); // Reset program filter when department changes
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Program Filter */}
        {programs.length > 0 && (
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Filter by Program
            </label>
            <select
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
              disabled={isSuperAdmin && departmentFilter === 'all'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
            >
              <option value="all">
                {isSuperAdmin && departmentFilter === 'all' 
                  ? 'Select a department first...' 
                  : 'All Programs'}
              </option>
              {programs
                .filter(prog => {
                  // If super admin selected a department, only show programs from that department
                  if (isSuperAdmin && departmentFilter !== 'all') {
                    const selectedDept = departments.find(d => d.name === departmentFilter);
                    return selectedDept && prog.department_id === selectedDept.id;
                  }
                  return true;
                })
                .map((prog) => (
                  <option key={prog.id} value={prog.code}>
                    {prog.code} - {prog.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Clear Filters Button */}
        {(departmentFilter !== 'all' || programFilter !== 'all') && (
          <div className="flex items-end">
            <button
              onClick={() => {
                setDepartmentFilter('all');
                setProgramFilter('all');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Status Filter Tabs */}
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200">
        <button
          onClick={() => setStatusFilter('active')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            statusFilter === 'active'
              ? 'text-blue-900 border-blue-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Active {stats.active > 0 && <span className="ml-1 text-xs bg-blue-900 text-white px-2 py-0.5 rounded-full">{stats.active}</span>}
        </button>
        <button
          onClick={() => setStatusFilter('closed')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            statusFilter === 'closed'
              ? 'text-blue-900 border-blue-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Closed
        </button>
        <button
          onClick={() => setStatusFilter('draft')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            statusFilter === 'draft'
              ? 'text-blue-900 border-blue-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Drafts
        </button>
        <button
          onClick={() => setStatusFilter('archived')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            statusFilter === 'archived'
              ? 'text-blue-900 border-blue-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Archived
        </button>
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            statusFilter === 'all'
              ? 'text-blue-900 border-blue-900'
              : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          All {visibleSurveys.length > 0 && <span className="ml-1 text-xs bg-gray-500 text-white px-2 py-0.5 rounded-full">{visibleSurveys.length}</span>}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Surveys</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Surveys</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.active}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <Send className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Responses</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalResponses}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Alumni</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pendingResponses}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Surveys List */}
      {(() => {
        // Sort surveys by status priority (active first) then by date
        const getSortedSurveys = (surveyList: Survey[]) => {
          const statusOrder: Record<string, number> = { active: 1, closed: 2, draft: 3, archived: 4 };
          return [...surveyList].sort((a, b) => {
            const statusDiff = statusOrder[a.status] - statusOrder[b.status];
            if (statusDiff !== 0) return statusDiff;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
        };

        // Filter surveys based on search term, status, department, and program
        const filteredSurveys = visibleSurveys.filter(survey => {
          const matchesSearch = 
            searchTerm === '' || 
            survey.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (survey.description && survey.description.toLowerCase().includes(searchTerm.toLowerCase()));
          
          const matchesStatus = 
            statusFilter === 'all' || 
            survey.status === statusFilter;
          
          // Department filter
          const matchesDepartment = 
            departmentFilter === 'all' ||
            (!survey.target_departments || survey.target_departments.length === 0) ||
            (survey.target_departments && survey.target_departments.includes(departmentFilter));
          
          // Program filter
          const matchesProgram = 
            programFilter === 'all' ||
            (!survey.target_programs || survey.target_programs.length === 0) ||
            (survey.target_programs && survey.target_programs.includes(programFilter));
          
          return matchesSearch && matchesStatus && matchesDepartment && matchesProgram;
        });

        // Apply sorting to filtered results
        const sortedFilteredSurveys = getSortedSurveys(filteredSurveys);

        if (sortedFilteredSurveys.length === 0) {
          return (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {visibleSurveys.length === 0 ? 'No surveys yet' : 'No matching surveys'}
              </h3>
              <p className="text-gray-600 mb-6">
                {visibleSurveys.length === 0 
                  ? 'Create your first survey to start collecting alumni feedback'
                  : 'Try adjusting your search or filter criteria'}
              </p>
              {visibleSurveys.length === 0 && (
                <button
                  onClick={openCreateModal}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Survey
                </button>
              )}
            </div>
          );
        }

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedFilteredSurveys.map((survey) => (
            <div
              key={survey.id}
              className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-lg transition"
            >
              <div className="flex justify-between items-start mb-4">
                <StatusBadge status={survey.status} />
                <span className="text-sm text-gray-500">
                  {new Date(survey.created_at).toLocaleDateString()}
                </span>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">{survey.title}</h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {survey.description || 'No description'}
              </p>

              <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>{survey.response_count || 0} responses</span>
              </div>

              {survey.deadline && (
                <div className="flex items-center gap-2 mb-4 text-sm text-orange-600">
                  <Calendar className="w-4 h-4" />
                  <span>Due: {new Date(survey.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              )}

              {surveyHealth[survey.id] && (
                <div className="mb-4">
                  <SurveyHealthBadge 
                    status={surveyHealth[survey.id].health_status}
                    brokenCount={surveyHealth[survey.id].broken_references}
                  />
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {survey.status === 'draft' && canModify(survey) && (
                  <button
                    onClick={() => openDeployModal(survey)}
                    className="flex-1 bg-green-50 text-green-700 px-3 py-2 rounded-lg hover:bg-green-100 transition text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <Send className="w-4 h-4" />
                    Deploy
                  </button>
                )}
                {survey.status === 'active' && canModify(survey) && (
                  <button
                    onClick={() => openCloseConfirm(survey)}
                    className="flex-1 bg-orange-50 text-orange-700 px-3 py-2 rounded-lg hover:bg-orange-100 transition text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <XCircle className="w-4 h-4" />
                    Close
                  </button>
                )}
                <button
                  onClick={() => viewResponses(survey)}
                  className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 transition text-sm font-medium flex items-center justify-center gap-1"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                {canModify(survey) && (
                <button
                  onClick={() => openDeleteConfirm(survey)}
                  className="bg-red-50 text-red-700 px-3 py-2 rounded-lg hover:bg-red-100 transition text-sm font-medium flex items-center justify-center"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                )}
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {showEditModal ? 'Edit Survey' : 'Create Survey'}
                </h2>
                {showEditModal && selectedSurvey?.status === 'active' && (
                  <p className="text-sm text-orange-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    This survey is currently active. You can save as draft or save & redeploy.
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Survey Title *
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., PEO Assessment Survey 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Describe the purpose of this survey..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status || 'draft'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Survey['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="draft">Draft</option>
                  {showEditModal && <option value="archived">Archived</option>}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Use the Deploy button to activate and send surveys to alumni
                </p>
              </div>

              {/* Questions */}
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Questions
                  </label>
                </div>

                {formData.questions && formData.questions.length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">No questions yet. Add your first question below.</p>
                  </div>
                )}

                <div className="space-y-4">
                  {formData.questions?.map((question, index) => (
                    <div key={question.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1 pt-2">
                          <button
                            onClick={() => moveQuestion(index, 'up')}
                            disabled={index === 0}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <GripVertical className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="flex-1 space-y-3">
                          <div className="flex gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-medium">
                              {question.type.replace('_', ' ').toUpperCase()}
                            </span>
                            <label className="flex items-center gap-1 text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={question.required}
                                onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                                className="rounded"
                              />
                              Required
                            </label>
                          </div>

                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) => updateQuestion(index, { text: e.target.value })}
                            disabled={!!question.profile_field}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                              question.profile_field ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Enter your question..."
                          />
                          {question.profile_field && (
                            <p className="text-xs text-gray-500 mt-1">
                              🔒 Question is locked (using profile field template)
                            </p>
                          )}

                          {/* Rating Scale Options */}
                          {question.type === 'rating' && (
                            <div className="space-y-2">
                              <div className="flex gap-4">
                                {!question.profile_field && (
                                  <div className="flex-1">
                                    <label className="block text-xs text-gray-600 mb-1">Scale (1 to...)</label>
                                    <input
                                      type="number"
                                      min="5"
                                      max="10"
                                      value={question.rating_scale || 10}
                                      onChange={(e) => updateQuestion(index, { rating_scale: parseInt(e.target.value) })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-600 mb-1">Link to PEO/PO (optional, can select multiple)</label>
                                  <div className="space-y-2">
                                    {/* Selected PEOs/POs */}
                                    {question.peo_ids && question.peo_ids.length > 0 && (
                                      <div className="flex flex-wrap gap-2">
                                        {question.peo_ids.map(peoId => {
                                          const peo = peos.find(p => p.id === peoId);
                                          if (!peo) return null;
                                          return (
                                            <span key={peoId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                              {peo.programs?.code} {peo.outcome_type}{peo.peo_number}
                                              <button
                                                onClick={() => {
                                                  const newPeoIds = question.peo_ids?.filter(id => id !== peoId);
                                                  updateQuestion(index, { peo_ids: newPeoIds?.length ? newPeoIds : undefined });
                                                }}
                                                className="text-blue-900 hover:text-blue-700"
                                              >
                                                ×
                                              </button>
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                    <select
                                      value=""
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          const currentPeoIds = question.peo_ids || [];
                                          if (!currentPeoIds.includes(e.target.value)) {
                                            updateQuestion(index, { peo_ids: [...currentPeoIds, e.target.value] });
                                          }
                                        }
                                      }}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    >
                                      <option value="">+ Add PEO/PO</option>
                                      {/* Group by program */}
                                      {Array.from(new Set(peos.map(p => p.programs?.code))).filter(Boolean).map(programCode => {
                                        const programPeos = peos.filter(p => p.programs?.code === programCode);
                                        const programName = programPeos[0]?.programs?.name;
                                        return (
                                          <optgroup key={programCode} label={`${programCode} - ${programName}`}>
                                            {programPeos.map(peo => {
                                              const isSelected = question.peo_ids?.includes(peo.id);
                                              return (
                                                <option key={peo.id} value={peo.id} disabled={isSelected} title={peo.description}>
                                                  {peo.outcome_type}{peo.peo_number}: {peo.title}
                                                </option>
                                              );
                                            })}
                                          </optgroup>
                                        );
                                      })}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Multiple Choice Options */}
                          {question.type === 'multiple_choice' && (
                            <div className="space-y-2">
                              <label className="block text-xs text-gray-600">Options:</label>
                              {question.profile_field ? (
                                // Read-only display when profile field is selected
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                                  {question.options?.map((option, optIndex) => (
                                    <div key={optIndex} className="flex items-center gap-2 text-sm text-gray-700">
                                      <span className="text-gray-400">•</span>
                                      <span>{option}</span>
                                    </div>
                                  ))}
                                  <p className="text-xs text-gray-500 mt-2">
                                    🔒 Options are locked (using profile field template)
                                  </p>
                                </div>
                              ) : (
                                // Editable when no profile field
                                <>
                                  {question.options?.map((option, optIndex) => (
                                    <div key={optIndex} className="flex gap-2">
                                      <input
                                        type="text"
                                        value={option}
                                        onChange={(e) => {
                                          const newOptions = [...(question.options || [])];
                                          newOptions[optIndex] = e.target.value;
                                          updateQuestion(index, { options: newOptions });
                                        }}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                        placeholder={`Option ${optIndex + 1}`}
                                      />
                                      <button
                                        onClick={() => {
                                          const newOptions = question.options?.filter((_, i) => i !== optIndex);
                                          updateQuestion(index, { options: newOptions });
                                        }}
                                        className="text-red-600 hover:text-red-800"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => {
                                      const newOptions = [...(question.options || []), ''];
                                      updateQuestion(index, { options: newOptions });
                                    }}
                                    disabled={(question.options || []).length >= 10}
                                    className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  >
                                    + Add Option {(question.options || []).length >= 10 && '(max 10)'}
                                  </button>
                                </>
                              )}
                            </div>
                          )}

                          {/* Profile Field Integration */}
                          <div className="space-y-2">
                            <label className="block text-xs text-gray-600">
                              Update Alumni Profile Field (optional):
                            </label>
                            <select
                              value={question.profile_field || ''}
                              onChange={(e) => {
                                const selectedField = e.target.value || undefined;
                                
                                // Apply template if profile field is selected
                                if (selectedField) {
                                  const updatedQuestion = applyProfileFieldTemplate(question, selectedField);
                                  // Update the entire question with template data
                                  updateQuestion(index, {
                                    profile_field: selectedField,
                                    text: updatedQuestion.text,
                                    options: updatedQuestion.options,
                                    rating_scale: updatedQuestion.rating_scale,
                                    required: updatedQuestion.required
                                  });
                                  toast.success(`Template applied! You can still edit the question.`, { duration: 3000 });
                                } else {
                                  // Just update the profile field
                                  updateQuestion(index, { profile_field: selectedField });
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              {getCompatibleProfileFields(question.type).map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {question.profile_field && (
                              <div className="text-xs space-y-1 bg-blue-50 border border-blue-200 rounded p-2">
                                <p className="text-green-600 font-medium">
                                  ✅ Template applied - Alumni answers will update their "{getCompatibleProfileFields(question.type).find(o => o.value === question.profile_field)?.label}" field
                                </p>
                                <p className="text-blue-600 font-medium">
                                  🔒 Question is locked to ensure database compatibility. To edit, remove the profile field selection.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => deleteQuestion(index)}
                          className="text-red-600 hover:text-red-800 pt-2"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Question Button */}
                <div className="mt-4">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        addQuestion(e.target.value as Question['type']);
                        e.target.value = '';
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                  >
                    <option value="">+ Add Question</option>
                    <option value="rating">Rating Scale</option>
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="text">Text Input</option>
                    <option value="yes_no">Yes/No</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              {/* Explanation for active survey editing */}
              {showEditModal && selectedSurvey?.status === 'active' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-gray-900 mb-1">Save as Draft:</p>
                        <p className="text-gray-600">Deactivates survey. Alumni can't access it until redeployed.</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 mb-1">Save & Redeploy:</p>
                        <p className="text-gray-600">Keeps survey active with current targeting. Clears existing responses.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              
              {/* Show different save options for editing active surveys */}
              {showEditModal && selectedSurvey?.status === 'active' ? (
                <>
                  <button
                    onClick={() => handleSaveSurvey(false)}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition flex items-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Save as Draft
                  </button>
                  <button
                    onClick={() => {
                      // Pre-populate deadline with current survey deadline
                      if (selectedSurvey?.deadline) {
                        setDeployDeadline(new Date(selectedSurvey.deadline).toISOString().slice(0, 16));
                      } else {
                        setDeployDeadline('');
                      }
                      setIsRedeployMode(true);
                      setShowEditModal(false);
                      setShowDeployDialog(true);
                    }}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    Save & Redeploy
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleSaveSurvey(false)}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Save Survey
                </button>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Dialog - Integrated Protection System */}
      {showDeployDialog && selectedSurvey && (
        <DeploySurveyDialog
          surveyId={selectedSurvey.id}
          surveyTitle={selectedSurvey.title}
          isOpen={showDeployDialog}
          isRedeployMode={isRedeployMode}
          isSuperAdmin={isSuperAdmin}
          programs={programs}
          departments={departments}
          availableYears={availableYears}
          onClose={() => {
            setShowDeployDialog(false);
            setSelectedSurvey(null);
            setIsRedeployMode(false);
          }}
          onConfirm={async (deploymentOptions) => {
            // Deploy the survey with targeting options
            try {
              console.log('[DEPLOY] Received deployment options:', deploymentOptions);
              
              // For dept admins, scope target departments to their allowed departments.
              // If they chose 'all alumni', treat it as 'all in my department'.
              const scopedDepts =
                !isSuperAdmin && allowedDepartments && allowedDepartments.length > 0
                  ? deploymentOptions.target_departments.length > 0
                    ? deploymentOptions.target_departments.filter(d => allowedDepartments!.includes(d))
                    : allowedDepartments
                  : deploymentOptions.target_departments;

              console.log('[DEPLOY] Scoped departments:', scopedDepts);
              console.log('[DEPLOY] Is super admin:', isSuperAdmin);
              console.log('[DEPLOY] Allowed departments:', allowedDepartments);

              // If redeploying, save the edited survey first
              if (isRedeployMode) {
                const surveyData: any = {
                  title: formData.title,
                  description: formData.description,
                  questions: formData.questions,
                  status: 'active',
                  target_batch: deploymentOptions.target_batch,
                  target_programs: deploymentOptions.target_programs,
                  target_departments: scopedDepts,
                  deadline: deploymentOptions.deadline,
                  updated_at: new Date().toISOString(),
                  redeployed_at: new Date().toISOString(),
                };

                console.log('[REDEPLOY] Saving survey data:', surveyData);

                const { error: updateError } = await supabaseClient
                  .from('surveys')
                  .update(surveyData)
                  .eq('id', selectedSurvey.id);

                if (updateError) throw updateError;

                // Delete all previous responses for this survey when redeploying
                console.log('[REDEPLOY] Deleting responses for survey:', selectedSurvey.id);
                
                const { count, error: deleteError } = await supabaseClient
                  .from('survey_responses')
                  .delete({ count: 'exact' })
                  .eq('survey_id', selectedSurvey.id);

                if (deleteError) {
                  console.error('[REDEPLOY] Error deleting responses:', deleteError);
                  toast.error('Survey updated but failed to reset responses');
                } else if (count && count > 0) {
                  toast.success(
                    `Survey redeployed! ${count} response${count > 1 ? 's' : ''} deleted.`,
                    { duration: 4000 }
                  );
                } else {
                  toast.success('Survey redeployed successfully!');
                }

                setIsRedeployMode(false);
              } else {
                // Normal deploy for draft surveys
                // Check if there are existing responses (could be from a previous close/reopen cycle)
                const { count: existingCount } = await supabaseClient
                  .from('survey_responses')
                  .select('*', { count: 'exact', head: true })
                  .eq('survey_id', selectedSurvey.id);

                // If there are existing responses, delete them before deploying
                if (existingCount && existingCount > 0) {
                  console.log('[DEPLOY] Found existing responses from previous deployment, deleting...');
                  
                  const { error: deleteError } = await supabaseClient
                    .from('survey_responses')
                    .delete()
                    .eq('survey_id', selectedSurvey.id);

                  if (deleteError) {
                    console.error('[DEPLOY] Error deleting old responses:', deleteError);
                    toast.error('Survey deployed but failed to clear old responses');
                  }
                }

                const updateData = {
                  status: 'active',
                  target_batch: deploymentOptions.target_batch,
                  target_programs: deploymentOptions.target_programs,
                  target_departments: scopedDepts,
                  deadline: deploymentOptions.deadline,
                  updated_at: new Date().toISOString()
                };

                console.log('[DEPLOY] Updating survey with data:', updateData);

                const { error: updateError } = await supabaseClient
                  .from('surveys')
                  .update(updateData)
                  .eq('id', selectedSurvey.id);

                if (updateError) throw updateError;

                if (existingCount && existingCount > 0) {
                  toast.success(`Survey deployed! ${existingCount} old response${existingCount > 1 ? 's' : ''} cleared.`);
                } else {
                  toast.success('Survey deployed successfully!');
                }
              }

              setShowDeployDialog(false);
              setSelectedSurvey(null);
              fetchSurveys();
              loadSurveyHealth();
            } catch (err: any) {
              toast.error(err.message || 'Failed to deploy survey');
            }
          }}
        />
      )}

      {/* OLD Deploy Modal - Replaced by DeploySurveyDialog above */}
      {false && showDeployDialog && selectedSurvey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">{isRedeployMode ? 'Save & Redeploy Survey' : 'Deploy Survey'}</h2>
              <button
                onClick={() => {
                  setShowDeployDialog(false);
                  setIsRedeployMode(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Deploying: <span className="font-semibold">{selectedSurvey?.title}</span>
                </p>
              </div>

              {selectedSurvey?.questions?.some((q: any) => q.peo_ids && q.peo_ids.length > 0) && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">PEO/PO Survey Detected</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This survey has questions linked to Program Educational Objectives (PEOs) or Program Outcomes (POs). 
                      Responses will affect alumni PEO scores used in job matching.
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                      <li><strong>Recent graduates (0–2 years):</strong> Scores serve as a baseline.</li>
                      <li><strong>Experienced alumni (3–5+ years):</strong> Scores reflect career-level proficiency.</li>
                    </ul>
                    <p className="text-sm text-amber-700 mt-2">
                      Make sure you are targeting the correct batch years and programs before deploying.
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Target Audience
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={deployTarget === 'all'}
                      onChange={() => setDeployTarget('all')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">All Alumni</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={deployTarget === 'specific'}
                      onChange={() => setDeployTarget('specific')}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Specific Groups</span>
                  </label>
                </div>
              </div>

              {deployTarget === 'specific' && (
                <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Graduation Years
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {availableYears.map(year => (
                        <label key={year} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedYears.includes(year)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedYears([...selectedYears, year]);
                              } else {
                                setSelectedYears(selectedYears.filter(y => y !== year));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{year}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Programs
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {programs.map(program => (
                        <label key={program.id} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedPrograms.includes(program.code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPrograms([...selectedPrograms, program.code]);
                              } else {
                                setSelectedPrograms(selectedPrograms.filter(p => p !== program.code));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{program.code}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Departments
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {departments.map(dept => (
                        <label key={dept.id} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedDepartments.includes(dept.name)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDepartments([...selectedDepartments, dept.name]);
                              } else {
                                setSelectedDepartments(selectedDepartments.filter(d => d !== dept.name));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{dept.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={deployDeadline}
                  onChange={(e) => setDeployDeadline(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Alumni must complete the survey before this date and time
                </p>
              </div>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowDeployDialog(false);
                  setIsRedeployMode(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeploy}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                {isRedeployMode ? 'Save & Redeploy' : 'Deploy Survey'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Modal */}
      {showCloseConfirm && selectedSurvey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-100 rounded-full p-3">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Close Survey</h2>
              </div>
              
              <p className="text-gray-600 mb-2">
                Are you sure you want to close <span className="font-semibold">"{selectedSurvey.title}"</span>?
              </p>
              
              <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                Alumni will no longer be able to access or submit responses to this survey. You will still be able to view all collected responses.
              </p>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseSurvey}
                className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition"
              >
                Close Survey
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedSurvey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-red-100 rounded-full p-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedSurvey.status === 'archived' ? 'Permanently Delete Survey' : 'Delete Survey'}
                </h2>
              </div>
              
              <p className="text-gray-600 mb-2">
                Are you sure you want to {selectedSurvey.status === 'archived' ? 'permanently delete' : 'delete'} <span className="font-semibold">"{selectedSurvey.title}"</span>?
              </p>
              
              {selectedSurvey.status === 'archived' ? (
                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <strong>Warning:</strong> This will permanently delete the survey and all {selectedSurvey.response_count || 0} response{selectedSurvey.response_count !== 1 ? 's' : ''}. This action cannot be undone.
                </p>
              ) : (selectedSurvey.status === 'active' || selectedSurvey.status === 'closed') ? (
                <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                  This survey is currently {selectedSurvey.status}. It will be archived to preserve response data.
                </p>
              ) : selectedSurvey.response_count && selectedSurvey.response_count > 0 ? (
                <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                  This survey has {selectedSurvey.response_count} response{selectedSurvey.response_count !== 1 ? 's' : ''}. It will be archived instead of deleted.
                </p>
              ) : (
                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  This action cannot be undone.
                </p>
              )}
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
              >
                {selectedSurvey.status === 'archived' ? 'Permanently Delete' : 
                 (selectedSurvey.status === 'active' || selectedSurvey.status === 'closed' || (selectedSurvey.response_count && selectedSurvey.response_count > 0)) ? 'Archive' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Responses Modal */}
      {showViewModal && selectedSurvey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">{selectedSurvey.title}</h2>
                    <StatusBadge status={selectedSurvey.status} />
                  </div>
                  {selectedSurvey.description && (
                    <p className="text-sm text-gray-600 mb-2">{selectedSurvey.description}</p>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    {selectedSurvey.deadline && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>Deadline: {new Date(selectedSurvey.deadline).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm ${
                      selectedSurvey.status === 'draft' 
                        ? 'bg-gray-100 text-gray-700 border border-gray-300'
                        : (selectedSurvey.target_programs?.length || selectedSurvey.target_departments?.length || selectedSurvey.target_batch?.length)
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      <Users className="w-4 h-4" />
                      <span className="font-semibold">Target:</span>
                      <span>
                        {selectedSurvey.status === 'draft' 
                          ? 'None (Not deployed)' 
                          : (() => {
                              const targets: string[] = [];
                              if (selectedSurvey.target_departments?.length) {
                                targets.push(`Dept: ${selectedSurvey.target_departments.join(', ')}`);
                              }
                              if (selectedSurvey.target_programs?.length) {
                                targets.push(`Prog: ${selectedSurvey.target_programs.join(', ')}`);
                              }
                              if (selectedSurvey.target_batch?.length) {
                                targets.push(`Years: ${selectedSurvey.target_batch.join(', ')}`);
                              }
                              return targets.length > 0 ? targets.join(' | ') : 'All Alumni';
                            })()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedSurvey.status === 'closed' && (
                    <>
                      <button
                        onClick={() => handleReopenToDraft(selectedSurvey)}
                        className="bg-yellow-50 text-yellow-700 px-4 py-2 rounded-lg hover:bg-yellow-100 transition text-sm font-medium flex items-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Reopen to Draft
                      </button>
                      <button
                        onClick={() => handleDuplicateSurvey(selectedSurvey)}
                        className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition text-sm font-medium flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Duplicate
                      </button>
                    </>
                  )}
                  {selectedSurvey.status === 'archived' && (
                    <button
                      onClick={() => handleDuplicateSurvey(selectedSurvey)}
                      className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition text-sm font-medium flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Duplicate
                    </button>
                  )}
                  {(selectedSurvey.status === 'draft' || selectedSurvey.status === 'active') && canModify(selectedSurvey) && (
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        openEditModal(selectedSurvey);
                      }}
                      className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition text-sm font-medium flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => { setShowViewModal(false); setResponseSearch('') }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Statistics Dashboard */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-700">Total Responses</p>
                      <p className="text-3xl font-bold text-blue-900 mt-1">{viewSurveyStats?.totalResponses || 0}</p>
                    </div>
                    <div className="bg-blue-200 rounded-full p-3">
                      <Users className="w-6 h-6 text-blue-700" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-700">Completion Rate</p>
                      <p className="text-3xl font-bold text-green-900 mt-1">{viewSurveyStats?.completionRate || 0}%</p>
                    </div>
                    <div className="bg-green-200 rounded-full p-3">
                      <Eye className="w-6 h-6 text-green-700" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-700">Pending Responses</p>
                      <p className="text-3xl font-bold text-orange-900 mt-1">
                        {viewSurveyStats?.pendingResponses || 0}
                      </p>
                    </div>
                    <div className="bg-orange-200 rounded-full p-3">
                      <AlertCircle className="w-6 h-6 text-orange-700" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions and Statistics */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Survey Questions & Statistics</h3>
                {selectedSurvey.questions && selectedSurvey.questions.length > 0 ? (
                  <div className="space-y-4">
                    {selectedSurvey.questions.map((question: any, idx: number) => {
                      // Calculate statistics for this question
                      const answers = surveyResponses.map(r => r.answers[question.id]).filter(a => a !== undefined && a !== null && a !== '');
                      const totalAnswers = answers.length;

                      return (
                        <div key={idx} className="bg-white border border-gray-200 rounded-lg p-5">
                          <div className="mb-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded">
                                {question.type === 'rating' ? 'Rating' : 
                                 question.type === 'multiple_choice' ? 'Multiple Choice' : 
                                 question.type === 'yes_no' ? 'Yes/No' : 'Text'}
                              </span>
                              {question.profile_field && (
                                <span className="inline-block bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded">
                                  📊 Updates Profile: {PROFILE_FIELD_OPTIONS.find(o => o.value === question.profile_field)?.label}
                                </span>
                              )}
                              {question.required && (
                                <span className="inline-block bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded">
                                  Required
                                </span>
                              )}
                            </div>
                            <p className="font-semibold text-gray-900">
                              Q{idx + 1}. {question.text}
                            </p>
                          </div>

                          {/* Statistics based on question type */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            {question.type === 'rating' && (
                              <div>
                                <p className="text-sm text-gray-600 mb-2">{totalAnswers} response(s)</p>
                                {totalAnswers > 0 && (() => {
                                  const numericAnswers = answers.map(a => Number(a));
                                  const avg = numericAnswers.reduce((sum, val) => sum + val, 0) / numericAnswers.length;
                                  const min = Math.min(...numericAnswers);
                                  const max = Math.max(...numericAnswers);
                                  
                                  return (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-4 text-sm">
                                        <span className="font-medium">Average: <span className="text-blue-600">{avg.toFixed(1)}</span></span>
                                        <span className="font-medium">Min: <span className="text-gray-600">{min}</span></span>
                                        <span className="font-medium">Max: <span className="text-gray-600">{max}</span></span>
                                      </div>
                                      {/* Distribution */}
                                      <div className="mt-3">
                                        <p className="text-xs text-gray-500 mb-1">Distribution:</p>
                                        <div className="space-y-1">
                                          {[...Array(10)].map((_, i) => {
                                            const rating = i + 1;
                                            const count = numericAnswers.filter(a => a === rating).length;
                                            const percentage = totalAnswers > 0 ? (count / totalAnswers) * 100 : 0;
                                            return count > 0 ? (
                                              <div key={rating} className="flex items-center gap-2">
                                                <span className="text-xs w-8">{rating}/10:</span>
                                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                  <div 
                                                    className="bg-blue-500 h-2 rounded-full" 
                                                    style={{ width: `${percentage}%` }}
                                                  ></div>
                                                </div>
                                                <span className="text-xs w-16 text-gray-600">{count} ({percentage.toFixed(0)}%)</span>
                                              </div>
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                            {question.type === 'multiple_choice' && (
                              <div>
                                <p className="text-sm text-gray-600 mb-2">{totalAnswers} response(s)</p>
                                {question.options && question.options.length > 0 && (
                                  <div className="space-y-2">
                                    {question.options.map((option: string, optIdx: number) => {
                                      const count = answers.filter(a => a === option).length;
                                      const percentage = totalAnswers > 0 ? (count / totalAnswers) * 100 : 0;
                                      return (
                                        <div key={optIdx} className="flex items-center gap-2">
                                          <span className="text-sm flex-1">{option}</span>
                                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                                            <div 
                                              className="bg-green-500 h-2 rounded-full" 
                                              style={{ width: `${percentage}%` }}
                                            ></div>
                                          </div>
                                          <span className="text-sm w-16 text-gray-600">{count} ({percentage.toFixed(0)}%)</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}

                            {question.type === 'yes_no' && (
                              <div>
                                <p className="text-sm text-gray-600 mb-2">{totalAnswers} response(s)</p>
                                {totalAnswers > 0 && (() => {
                                  const yesCount = answers.filter(a => a === 'Yes' || a === 'yes').length;
                                  const noCount = answers.filter(a => a === 'No' || a === 'no').length;
                                  const yesPercentage = totalAnswers > 0 ? (yesCount / totalAnswers) * 100 : 0;
                                  const noPercentage = totalAnswers > 0 ? (noCount / totalAnswers) * 100 : 0;
                                  
                                  return (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm w-12">Yes:</span>
                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                          <div 
                                            className="bg-green-500 h-2 rounded-full" 
                                            style={{ width: `${yesPercentage}%` }}
                                          ></div>
                                        </div>
                                        <span className="text-sm w-16 text-gray-600">{yesCount} ({yesPercentage.toFixed(0)}%)</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm w-12">No:</span>
                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                          <div 
                                            className="bg-red-500 h-2 rounded-full" 
                                            style={{ width: `${noPercentage}%` }}
                                          ></div>
                                        </div>
                                        <span className="text-sm w-16 text-gray-600">{noCount} ({noPercentage.toFixed(0)}%)</span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                            {question.type === 'text' && (
                              <div>
                                <p className="text-sm text-gray-600">{totalAnswers} text response(s)</p>
                                {totalAnswers === 0 && (
                                  <p className="text-sm text-gray-400 italic mt-1">No responses yet</p>
                                )}
                              </div>
                            )}

                            {totalAnswers === 0 && question.type !== 'text' && (
                              <p className="text-sm text-gray-400 italic">No responses yet</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No questions in this survey</p>
                )}
              </div>

              {/* Responses */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Individual Responses</h3>
                {surveyResponses.length > 0 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={responseSearch}
                      onChange={e => setResponseSearch(e.target.value)}
                      placeholder="Search by name or program..."
                      className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-60"
                    />
                    {responseSearch && (
                      <button
                        onClick={() => setResponseSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {surveyResponses.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No responses yet</p>
                </div>
              ) : responseSearch.trim() && surveyResponses.filter(r => {
                  const q = responseSearch.toLowerCase()
                  const fullName = `${r.alumni_profiles?.first_name ?? ''} ${r.alumni_profiles?.last_name ?? ''}`.toLowerCase()
                  const program = (r.alumni_profiles?.program ?? '').toLowerCase()
                  return fullName.includes(q) || program.includes(q)
                }).length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-sm">No responses match &ldquo;{responseSearch}&rdquo;</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(responseSearch.trim() ? surveyResponses.filter(r => {
                    const q = responseSearch.toLowerCase()
                    const fullName = `${r.alumni_profiles?.first_name ?? ''} ${r.alumni_profiles?.last_name ?? ''}`.toLowerCase()
                    const program = (r.alumni_profiles?.program ?? '').toLowerCase()
                    return fullName.includes(q) || program.includes(q)
                  }) : surveyResponses).map((response) => (
                    <div key={response.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-semibold text-gray-900 text-lg">
                            {response.alumni_profiles?.first_name} {response.alumni_profiles?.last_name}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                            <span>{response.alumni_profiles?.program || 'Unknown Program'}</span>
                            <span>•</span>
                            <span>Class of {response.alumni_profiles?.year_graduated || 'N/A'}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(response.submitted_at).toLocaleString()}
                        </p>
                      </div>

                      <div className="space-y-3">
                        {selectedSurvey.questions.map((question: any, idx: number) => {
                          const answer = response.answers[question.id];
                          return (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4">
                              <p className="font-medium text-gray-900 mb-2">
                                Q{idx + 1}. {question.text}
                              </p>
                              <div className="text-gray-700">
                                {question.type === 'rating' ? (
                                  <div className="flex items-center gap-2">
                                    <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                                      {answer} / 10
                                    </div>
                                  </div>
                                ) : question.type === 'multiple_choice' ? (
                                  <div className="bg-white border border-gray-200 rounded px-3 py-2">
                                    {answer}
                                  </div>
                                ) : question.type === 'yes_no' ? (
                                  <div className={`inline-block px-3 py-1 rounded-full font-medium ${
                                    answer === 'Yes' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {answer}
                                  </div>
                                ) : (
                                  <div className="bg-white border border-gray-200 rounded p-3 whitespace-pre-wrap">
                                    {answer || <span className="text-gray-400 italic">No response</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSurveys;
